import { createAction } from 'redux-actions';
import { timeMonth, timeMinute } from 'd3-time';
import { isoParse, timeFormat } from 'd3-time-format';

import {
  descriptionCreator,
  idCreator,
  metaCreator,
  payloadCreator,
} from './creator';
import { checkForCachedDataAction, requestAction } from './request';
import fetch, {
  Api,
  defaultRequestConfig,
  getJson,
  handleError,
} from './fetch';

const fhirDayFormat = timeFormat('%Y-%m-%d');
const fhirTimeParse = isoParse;

export const supportedTypes = ['Observation', 'MedicationAdministration'];

export const fhirFetchAction = createAction('GET_FHIR_DATA', payloadCreator, metaCreator);

const itemAdapter = (item, type, store) => {
  const parsed = {
    raw: item,
  };
  if (type === 'Observation') {
    parsed.time = fhirTimeParse(item.effectiveDateTime).toISOString();
    // TODO: simplify coding, map to only codes!
    const codes = item.code.coding.map(c => c.code);
    if (codes.indexOf('14745-4') >= 0) {
      parsed.glucose = item.valueQuantity.value;
      parsed.unit = item.valueQuantity.unit;
      if (!store.sgv) {
        store.sgv = [];
      }
      store.sgv.push(parsed);
    } else if ((codes.indexOf('15074-8') >= 0) || (codes.indexOf('14743-9') >= 0)) {
      parsed.glucose = item.valueQuantity.value;
      parsed.unit = item.valueQuantity.unit;
      if (!store.smbg) {
        store.smbg = [];
      }
      store.smbg.push(parsed);
    } else if ((codes.indexOf('9060-5') >= 0) || (codes.indexOf('9059-7') >= 0)) {
      if (item.valueQuantity) {
        parsed.carbohydrate = item.valueQuantity.value;
      } else if (item.component) {
        item.component.forEach((component) => {
          const componentCodes = component.code.coding.map(coding => coding.code);
          if (componentCodes.indexOf('9060-5') >= 0 || codes.indexOf('9059-7') >= 0) {
            parsed.carbohydrate = component.valueQuantity.value;
          }
        });
      }
      if (!store.nutrition) {
        store.nutrition = [];
      }
      store.nutrition.push(parsed);
    }
  } else if (type === 'MedicationAdministration') {
    // check whether there's a point in time or a duration indicated
    // TODO: check the medicationCodeableConcept for insulin type (and to ignore other medicines!)
    if (item.effectivePeriod) {
      parsed.time = fhirTimeParse(item.effectivePeriod.start);
      parsed.endTime = fhirTimeParse(item.effectivePeriod.end);
      parsed.duration = timeMinute.count(parsed.time, parsed.endTime);
    } else {
      parsed.time = fhirTimeParse(item.effectiveDateTime);
    }
    parsed.amount = item.dosage.dose;
    if (item.rateRatio) {
      // Parse the TBR setting from here.
    }
    if (item.rateQuantity) {
      // Parse the base rate from here?
    }
  }
  //  console.log('Parsed FHIR item', item, parsed);
  return store;
};

const getUrl = ({ type, params }) => {
  const dateParamName = type === 'MedicationAdministration' ? 'effective-time' : 'date';
  const startTimeString = params.startTime
    ? `&${dateParamName}=ge${fhirDayFormat(params.startTime)}`
    : '';
  const endTimeString = params.endTime
    ? `&${dateParamName}=lt${fhirDayFormat(params.endTime)}`
    : '';
  const url = `${Api}/fhirdata/${
    type
  }?_count=${
    // type === 'Observation' ? 10000 : 3000
    1000
  }&_sort=-${
    dateParamName
  }${
    startTimeString
  }${
    endTimeString
  }`;
  return url;
};

const fetchData = ({ type, params, url }) => {
  return fetch(url || getUrl({ type, params }), defaultRequestConfig)
    .then(handleError)
    .then(getJson)
    .then(json => {
      if (!json.entry) {
        return [];
      }
      const processed = json.entry.reduce((store, item) => {
        try {
          itemAdapter(item.resource, type, store);
        } catch (error) {
          console.error('Failed with item', item, error);
        }
        return store;
      }, {});
      if (json.link) {
        let nextLink;
        json.link.some((link) => {
          if (link.relation === 'next') {
            nextLink = link.url;
            return true;
          }
          return false;
        })
        if (nextLink) {
          processed.next = nextLink;
        }
      }
      return processed;
    });
};

const defaultParams = {
  startTime: timeMonth(new Date()),
};

export const getFhirData = ({ type, params = defaultParams, url, counter = 0 }) => ((dispatch) => {
  if (!type) {
    return Promise.reject(new Error('No type specified'));
  }
  const id = idCreator();
  const description = descriptionCreator({ source: `FHIR${url ? ` - ${url}` : ''}`, type, params });
  return checkForCachedDataAction(requestAction({ id, description }), dispatch)
    .then(() => fetchData({ type, params, url }))
    .then((data) => {
      return data;
    })
    .then((data) => {
      // Check if there's a next link present
      const stored = dispatch(fhirFetchAction({ id, description, data }));
      if (data.next && counter < 20) {
        counter += 1;
        const search = data.next.substring(data.next.indexOf('?'));
        const nextUrl = `${Api}/fhirdata/${type}${search}`;
        return dispatch(getFhirData({type, params, url: nextUrl, counter }));
      }
      return stored;
    })
    .catch(error => dispatch(fhirFetchAction({ id, description, error: error.message || error })));
});

export default getFhirData;
